import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { memo, useMemo } from 'react';
import type { IMessage, BubbleProps } from 'react-native-gifted-chat';

import { HighlightedMessageText } from './HighlightedMessageText';
import { StatementReferencesFooter } from './StatementReferencesFooter';
import { useCitationRequestForMessage } from '../../hooks/useCitationRequestForMessage';
import { useCitationRequestReviewStoreCleanup } from '../../hooks/useCitationRequestReviewStoreCleanup';
import { useExtractedStatementsImpression } from '../../hooks/useExtractedStatementsImpression';
import { useStatementReferencesStoreCleanup } from '../../hooks/useStatementReferencesStoreCleanup';
import { mergeStatementReferences } from '../../model/extractedStatements';
import { computeReferenceNumbering } from '../../model/numbering';
import type { StatementReferenceState } from '../../model/statementReferenceState';
import { AddReferencesPromptBubble } from '../prompt/AddReferencesPromptBubble';
import { StatementTriageSheet } from '../wizard/StatementTriageSheet';

import {
  ChatCustomView,
  isMessageFromCurrentUser,
  type CustomBubbleProps,
  type ICustomMessage,
} from '@/components/chat';
import { ReferenceSheet } from '@/components/chat/bubbles/references/ReferenceSheet';
import {
  buildInlineCitations,
  openableNumbers as getOpenableNumbers,
  withOpenableEntries,
} from '@/components/chat/bubbles/references/inlineCitations';
import { buildMessageReferences } from '@/components/chat/bubbles/references/messageReferences';
import { useReferenceSheet } from '@/components/chat/bubbles/references/useReferenceSheet';
import { BubbleTimestamp } from '@/components/chat/chatRenderers';
import { ChatBubble } from '@/components/chat/message-list/ChatBubble';
import { Box } from '@/components/ui/box';
import { HStack } from '@/components/ui/hstack';
import { useMessageLinkOpener } from '@/hooks/chat/useMessageLinkOpener';
import { useMessageShares } from '@/hooks/chat/useMessagePublications';
import type { useColors } from '@/hooks/useColors';

type Colors = ReturnType<typeof useColors>['colors'];

interface ExtractedStatementsBubbleProps {
  bubbleProps: CustomBubbleProps;
  colors: Colors;
  closedConversation: boolean;
  isAuthor: boolean;
  conversationId: string;
  userId: string;
  /** The message's statements and references, from the conversation's
   * statement-references query. */
  statementReferences: StatementReferenceState | undefined;
  /**
   * Neutralizes GiftedChat's `flex: 1` bubble root so the bubble sizes to
   * content. Required when the bubble renders under a caption line (inside
   * `MessageBubbleWithLabel`); without it the `flex: 1` root stretches the
   * bubble vertically inside the caption stack on native. Defaults to `false`
   * for the standalone (non-caption) render path.
   */
  shrinkWidthToContent?: boolean;
  /**
   * Whether to surface the add-references prompt beneath the bubble. Decided by
   * the caller, which knows the viewer's role and relay context; this bubble
   * owns only the fact that the prompt belongs to it. It is an affordance of
   * this bubble, not a message type of its own, so it lives here rather than as
   * a row-level decoration the dispatcher has to special-case.
   */
  showAddReferencesPrompt?: boolean;
  /** Whether a link in the text is the inquiry recipient's, which reports as
   * a reference when opened, as it does in a plain message. */
  linksAreReferences?: boolean;
}

/**
 * Bubble variant for expert answer messages with approved, non-empty
 * extracted statements.
 * Renders the standard text bubble chrome with each cited statement
 * underlined. Its chips and underlined words open the reference sheet at
 * that statement's reference; there is no separate statement view.
 */
export const ExtractedStatementsBubble = memo(function ExtractedStatementsBubble({
  bubbleProps,
  colors,
  closedConversation,
  isAuthor,
  conversationId,
  userId,
  statementReferences,
  shrinkWidthToContent = false,
  showAddReferencesPrompt = false,
  linksAreReferences = false,
}: ExtractedStatementsBubbleProps) {
  const message = bubbleProps.currentMessage as ICustomMessage;

  const messageSid = message.messageId;

  const extractedStatements = statementReferences?.extractedStatements ?? [];

  const position: 'left' | 'right' = bubbleProps.position ?? 'left';

  /**
   * Author flag — `true` when the current viewer (`userId`) is the
   * sender of this message. Decides whether the author's pending
   * references are numbered and whether the triage sheet and
   * add-references prompt can mount.
   */
  const viewerIsAuthor = useMemo(
    () => isMessageFromCurrentUser(message, userId),
    [message, userId]
  );

  /**
   * Per-statement references attached to this message. Empty array
   * means "no references attached yet".
   */
  const attachedStatementReferences = statementReferences?.attachedStatementReferences ?? [];

  /**
   * Per-statement editorial-intent statuses for the citation triage
   * surface. The API returns them to the author only, and the triage
   * sheet is only mounted when `viewerIsAuthor`.
   */
  const statementCitationStatuses = statementReferences?.statementCitationStatuses;

  /**
   * Per-statement free-text help requests captured by the author during
   * the citation-triage wizard's Review phase. The API returns them to
   * the author only. Forwarded into the wizard + standalone
   * review sheets so each statement group can render the
   * `StatementReferenceHelpRequestRow`.
   */
  const statementReferenceHelpRequests = statementReferences?.statementReferenceHelpRequests;

  /**
   * Merge resolved (`attachedStatementReferences`) and pending
   * (`statementReferenceHelpRequests`) entries into a single renderable
   * set, gated by viewer role. The author sees pending entries as
   * first-class references on the body, sheet, and footnote footer;
   * the recipient only sees resolved entries until staff attach a URL.
   * See `mergeStatementReferences` for the per-statementIndex rule.
   */
  const renderableReferences = useMemo(
    () =>
      mergeStatementReferences({
        attached: attachedStatementReferences,
        helpRequests: statementReferenceHelpRequests,
        viewerRole: viewerIsAuthor ? 'author' : 'recipient',
      }),
    [attachedStatementReferences, statementReferenceHelpRequests, viewerIsAuthor]
  );

  const { publications, shareEntries } = useMessageShares(message);

  /** Statement chip numbers, from the server's numbering, with the author's
   * pending references numbered after it. */
  const numbering = useMemo(
    () => computeReferenceNumbering(extractedStatements, renderableReferences, shareEntries),
    [extractedStatements, renderableReferences, shareEntries]
  );

  /** Every reference the reader can open, in the server's order: the list the
   * footer indexes and the reference sheet pages through. */
  const references = useMemo(
    () => buildMessageReferences(shareEntries, publications, extractedStatements),
    [shareEntries, publications, extractedStatements]
  );

  const referenceSheet = useReferenceSheet({
    references,
    conversationId,
    messageSid,
    totalAttached: numbering.footnoteCount,
  });

  const openableNumbers = useMemo(() => getOpenableNumbers(references), [references]);

  const openLink = useMessageLinkOpener({
    conversationId,
    messageSid,
    isReference: linksAreReferences,
  });

  const inlineCitations = useMemo(
    () => buildInlineCitations(message.text ?? '', withOpenableEntries(shareEntries, references)),
    [message.text, shareEntries, references]
  );

  /**
   * Remounts the message text when its chips change, as when a statement
   * re-sync renumbers the message. iOS can keep the nested text's first
   * measurement after its content changes, clipping the last line.
   */
  const chipLayoutKey = useMemo(
    () =>
      [
        [...numbering.byStatement.entries()]
          .map(([statementIndex, numbers]) => `${statementIndex}:${numbers.join('.')}`)
          .join(','),
        inlineCitations.map((citation) => `${citation.end}:${citation.number}`).join(','),
      ].join('|'),
    [numbering.byStatement, inlineCitations]
  );

  /** Gates the footer and the in-text timestamp that replaces GiftedChat's
   * own. Pending references count: the author still sees them listed. */
  const hasFootnotes = numbering.pending.length + references.length > 0;

  useExtractedStatementsImpression({
    messageSid,
    statementCount: extractedStatements.length,
    referenceCount: numbering.footnoteCount,
    statementsWithReferences: numbering.byStatement.size,
    viewerRole: viewerIsAuthor ? 'author' : 'recipient',
  });

  useStatementReferencesStoreCleanup(messageSid);

  /**
   * Active async citation request for this message (if any). Only
   * used here to scope the per-request review store cleanup on
   * unmount — the wizard (`StatementTriageSheet`) reads the same
   * server state itself to drive its phase. `undefined` when there
   * is no active request.
   */
  const { citationRequest: activeCitationRequest } = useCitationRequestForMessage({
    conversationId,
    messageSid,
  });

  useCitationRequestReviewStoreCleanup(activeCitationRequest?.id);

  return (
    <>
      <ChatBubble
        shrinkWidthToContent={shrinkWidthToContent}
        hideBottomContainer={hasFootnotes}
        bubbleProps={{
          ...bubbleProps,
          /** When references exist, we render the timestamp ourselves as
           * the last row inside `renderMessageText` (see below) and ask
           * `ChatBubble` to hide GiftedChat's bottom container entirely.
           * Suppress `renderTime` so it doesn't try to render into the
           * now-hidden bottom container either. */
          ...(hasFootnotes ? { renderTime: () => null } : {}),
          renderMessageText: (textProps: BubbleProps<IMessage>) => {
            const text = (textProps.currentMessage as ICustomMessage)?.text ?? '';

            const messageTextColorClassName =
              position === 'right' ? 'text-typography-white' : 'text-typography-900';

            return (
              <Box className={hasFootnotes ? 'px-3 pb-1 pt-2' : 'px-3 pt-2'}>
                <HighlightedMessageText
                  key={chipLayoutKey}
                  messageSid={messageSid}
                  body={text}
                  extractedStatements={extractedStatements}
                  textClassName={`${messageTextColorClassName} leading-snug`}
                  position={position}
                  numbering={numbering}
                  openableNumbers={openableNumbers}
                  onOpenStatementReference={referenceSheet.openNumber}
                  inlineCitations={inlineCitations}
                  onOpenCitation={referenceSheet.openCitation}
                  onOpenLink={openLink}
                />
                {hasFootnotes ? (
                  <>
                    <StatementReferencesFooter
                      pending={numbering.pending}
                      references={references}
                      position={position}
                      onOpenReference={referenceSheet.openReference}
                    />
                    {/* Timestamp as its own right-aligned row at the bottom
                     * of the message-text Box. GiftedChat's bottom container
                     * is hidden via `hideBottomContainer` so this row sits
                     * at the bubble's true bottom-right corner. */}
                    <HStack className="mt-2 justify-end">
                      <BubbleTimestamp currentMessage={message} position={position} />
                    </HStack>
                  </>
                ) : null}
              </Box>
            );
          },
        }}
        colors={colors}
        renderCustomContent={(customViewProps) => {
          const props = customViewProps as CustomBubbleProps;

          const files = (props.currentMessage as ICustomMessage | undefined)?.files;

          /** Skip the wrapping `<Box>` when there's nothing to render. The
           * empty Box still occupies a row in the bubble's vertical stack
           * and pushes the bottom container away from the message body. */
          if (!files || files.length === 0) {
            return null;
          }

          return (
            <Box className="px-3">
              <ChatCustomView
                {...props}
                closedConversation={closedConversation}
                isAuthor={isAuthor}
                conversationId={conversationId}
                userId={userId}
              />
            </Box>
          );
        }}
      />
      {references.length > 0 ? (
        <ReferenceSheet
          references={references}
          openIndex={referenceSheet.openIndex}
          onIndexChange={referenceSheet.setIndex}
          onClose={referenceSheet.close}
          onOpenSource={referenceSheet.openSource}
          viewerIsAuthor={viewerIsAuthor}
        />
      ) : null}
      {showAddReferencesPrompt ? (
        <AddReferencesPromptBubble
          messageSid={messageSid}
          conversationId={conversationId}
          statementCount={extractedStatements.length}
          attachedStatementReferences={attachedStatementReferences ?? []}
          extractedStatements={extractedStatements}
          statementCitationStatuses={statementCitationStatuses}
          statementReferenceHelpRequests={statementReferenceHelpRequests}
        />
      ) : null}
      {viewerIsAuthor && isWeb ? (
        <StatementTriageSheet
          conversationId={conversationId}
          messageSid={messageSid}
          extractedStatements={extractedStatements}
          statementCitationStatuses={statementCitationStatuses}
          attachedStatementReferences={attachedStatementReferences}
          statementReferenceHelpRequests={statementReferenceHelpRequests}
        />
      ) : null}
    </>
  );
});
